import { FC, useMemo } from "react";
import useEvent from "react-use-event-hook";

import { useMapColors } from "@ryde/redux";
import { getLiveMapRouteLineColor } from "@ryde/shared";

import { FramerTooltip } from "../../../../common";
import {
  ClickableColorCircle,
  IndicatorSlot,
  LiveMapUnselectedDotsIcon,
} from "./live-map-color-indicator.styled";

export interface ILiveMapColorIndicatorProps {
  rideId: number;
  /** From ride `computedData.courseLineColor` when course defines a line color. */
  courseLineColor?: string | undefined;
  isSelected: boolean;
  onToggle: () => void;
  tooltipDisabled?: boolean;
  tooltipContent: string;
}

/** Left of the live-map row checkbox: BlurOn dots when unselected, map palette circle when selected. */
export const LiveMapColorIndicator: FC<ILiveMapColorIndicatorProps> = ({
  rideId,
  courseLineColor,
  isSelected,
  onToggle,
  tooltipDisabled,
  tooltipContent,
}) => {
  const { getMapColorByRideId } = useMapColors();
  const mapColor = useMemo(
    () =>
      getLiveMapRouteLineColor({
        rideId,
        courseLineColor,
        getFallbackColorByRideId: getMapColorByRideId,
      }),
    [courseLineColor, getMapColorByRideId, rideId],
  );

  const handleClick = useEvent((e: React.MouseEvent): void => {
    e.stopPropagation();
    onToggle();
  });

  const handleKeyDown = useEvent((e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  });

  return (
    <FramerTooltip tooltipDisabled={tooltipDisabled} content={tooltipContent}>
      <IndicatorSlot>
        {isSelected ? (
          <ClickableColorCircle
            className="live-map-color-indicator-circle color-circle-single-select"
            $color={mapColor}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            data-e2e="live-map-color-indicator-circle"
          />
        ) : (
          <LiveMapUnselectedDotsIcon
            className="color-circle-single-select"
            aria-label={tooltipContent}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          />
        )}
      </IndicatorSlot>
    </FramerTooltip>
  );
};
